import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { seoRoutes } from "./seo-routes";

/**
 * Client-side head sync: the prerendered HTML carries each route's meta for
 * crawlers, but a visitor navigating client-side would keep the previous
 * page's title/description. This mirrors `src/seo-routes.ts` into the live
 * document on every navigation (and on first load in `vite dev`, where no
 * prerender runs).
 */
export function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const route = seoRoutes.find((entry) => entry.path === pathname);
    const meta = route?.meta;
    if (!meta) return;

    if (meta.title) document.title = meta.title;

    setMeta("name", "description", meta.description);
    setMeta(
      "name",
      "robots",
      meta.index === false ? "noindex, nofollow" : "index, follow",
    );
    setLink(
      "canonical",
      meta.canonical ?? new URL(pathname, window.location.origin).toString(),
    );
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.description);
    setMeta("property", "og:type", meta.type ?? "website");
    if (meta.image) {
      setMeta(
        "property",
        "og:image",
        new URL(meta.image, window.location.origin).toString(),
      );
      setMeta(
        "name",
        "twitter:image",
        new URL(meta.image, window.location.origin).toString(),
      );
    }
    setMeta(
      "name",
      "twitter:card",
      meta.image ? "summary_large_image" : "summary",
    );
  }, [pathname]);

  return null;
}

function setMeta(
  key: "name" | "property",
  name: string,
  content: string | undefined,
) {
  if (!content) return;
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${key}="${cssEscape(name)}"]`,
  );
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(key, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

function cssEscape(value: string): string {
  return value.replace(/"/g, '\\"');
}
